import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";

const euro = (n) => new Intl.NumberFormat("it-IT", {
  style: "currency", currency: "EUR"
}).format(Number(n || 0));

export default function RichiesteFatturazioneTab() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState("in_attesa_conferma");
  const [errore, setErrore] = useState("");

  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["richieste-fatturazione"],
    queryFn: () => api.get("/fatturazione/richieste").then(r => r.data),
  });

  const mutation = useMutation({
    mutationFn: ({ id, approvata }) =>
      api.post(`/fatturazione/richieste/${id}/conferma`, {
        approvata,
        nota: "",
      }),
    onSuccess: () => {
      setErrore("");
      qc.invalidateQueries({ queryKey: ["richieste-fatturazione"] });
    },
    onError: (e) => setErrore(
      e.response?.data?.detail || "Operazione non riuscita"
    ),
  });

  const eliminaMutation = useMutation({
    mutationFn: (id) => api.delete(`/fatturazione/richieste/${id}`),
    onSuccess: () => {
      setErrore("");
      qc.invalidateQueries({ queryKey: ["richieste-fatturazione"] });
    },
    onError: (e) => setErrore(
      e.response?.data?.detail || "Eliminazione non riuscita"
    ),
  });

  const elimina = (r) => {
    if (!window.confirm(
      `ELIMINARE la richiesta di fatturazione?\\n\\nCantiere: ${r.cantiere_nome || r.cantiere_id || "—"}\\nImporto: ${euro(r.importo)}\\n\\nQuesta operazione non può essere annullata.`
    )) return;
    eliminaMutation.mutate(r.richiesta_id);
  };

  const richieste = (Array.isArray(data) ? data : []).filter(r =>
    filtro === "tutte" || r.stato === filtro
  );

  const conferma = (r, approvata) => {
    const azione = approvata ? "APPROVARE" : "RIFIUTARE";
    if (!window.confirm(
      `${azione} la richiesta ${r.richiesta_id}?`
    )) return;
    mutation.mutate({ id: r.richiesta_id, approvata });
  };

  return (
    <div className="space-y-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold">Richieste di fatturazione</h2>
        <select
          className="border rounded px-3 py-2 text-sm"
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        >
          <option value="in_attesa_conferma">In attesa</option>
          <option value="confermata">Confermate</option>
          <option value="rifiutata">Rifiutate</option>
          <option value="fatturata">Fatturate</option>
          <option value="tutte">Tutte</option>
        </select>
      </div>

      {errore && <p className="text-red-700">{errore}</p>}
      {isLoading && <p>Caricamento richieste...</p>}
      {isError && <p className="text-red-700">Impossibile caricare le richieste.</p>}

      {!isLoading && !isError && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-3 text-left">Cantiere / Cliente</th>
                <th className="p-3 text-left">Causale</th>
                <th className="p-3 text-left">Fornitore / Subappaltatore</th>
                <th className="p-3 text-right">Importo da fatturare</th>
                <th className="p-3 text-right">Importo fornitore</th>
                <th className="p-3 text-left">Stato</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {richieste.map(r => (
                <tr key={r.richiesta_id} className="border-b">
                  <td className="p-3">{r.origine === "opportunita_promemoria" ? (r.cliente || "Opportunità commerciale") : (r.cantiere_nome || r.cantiere_id || "—")}</td>
                  <td className="p-3">{r.descrizione || r.causale || r.voce_nome || "—"}</td>
                  <td className="p-3">{r.fornitore_nome || "—"}</td>
                  <td className="p-3 text-right">{euro(r.importo)}</td>
                  <td className="p-3 text-right">{r.importo_fornitore !== undefined && r.importo_fornitore !== "" ? euro(r.importo_fornitore) : "—"}</td>
                  <td className="p-3">{r.stato}</td>
                  <td className="p-3 text-right">
                    {r.stato === "in_attesa_conferma" && (
                      <div className="flex justify-end gap-2">
                        <button
                          disabled={mutation.isPending}
                          onClick={() => conferma(r, true)}
                          className="bg-green-700 text-white px-3 py-1 rounded disabled:opacity-50"
                        >
                          Approva
                        </button>
                        <button
                          disabled={mutation.isPending}
                          onClick={() => conferma(r, false)}
                          className="bg-red-700 text-white px-3 py-1 rounded disabled:opacity-50"
                        >
                          Rifiuta
                        </button>
                      </div>
                    )}
                    {r.stato !== "fatturata" && !r.fattura_id && !r.fattura_numero && (
                      <button
                        type="button"
                        title="Elimina richiesta"
                        aria-label={`Elimina richiesta ${r.richiesta_id}`}
                        disabled={mutation.isPending || eliminaMutation.isPending}
                        onClick={() => elimina(r)}
                        className="ml-2 inline-flex items-center justify-center rounded border border-red-300 p-2 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 6h18M8 6V4h8v2m3 0-1 14H6L5 6m5 4v7m4-7v7" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {richieste.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500">
                    Nessuna richiesta per il filtro selezionato.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
